import { useCallback, useMemo } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { DateTime, TimeZone } from 'timezonecomplete';

import type { IDateFormatter } from '@/types/date';

import { selectUserTimezone } from '@/store/selectors/authSelectors';
import { useAppSelector } from '@/hooks';
import { getTimezoneAbbreviation } from '@/helpers/timezone';

const useTimezoneSelector = createSelector(selectUserTimezone, timezoneName => ({
  timezoneName,
}));

interface IUseTimeZone {
  formatDateTime: IDateFormatter;
  timezoneName: string;
  timezoneAbbreviation: string;
}

const useTimezone = (): IUseTimeZone => {
  const { timezoneName } = useAppSelector(useTimezoneSelector);

  const timezoneAbbreviation = useMemo<IUseTimeZone['timezoneAbbreviation']>(
    () => getTimezoneAbbreviation(timezoneName),
    [timezoneName],
  );

  /**
   * Takes UTC ISO timestamp and applies specified formatting and timezone convertion according to timezone from user profile.
   * Timezone fallback is taken from system settings if there isn't one defined in the profile.
   *
   * @param date in UTC
   * @param format formatting template
   * @return formatted date
   */
  const formatDateTime = useCallback<IUseTimeZone['formatDateTime']>(
    (date, format) => {
      const timezoneAwareDate = new DateTime(date, TimeZone.utc()).toZone(TimeZone.zone(timezoneName));

      return timezoneAwareDate.format(format);
    },
    [timezoneName],
  );

  return { formatDateTime, timezoneName, timezoneAbbreviation };
};

export { useTimezone };
